import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useApp } from "@/context/AppContext";
import { GroupBadge } from "@/components/GroupBadge";
import { initials } from "@/lib/format";
import { ChevronsUpDown, Check, Sparkles, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

function Avatar({ p, ring }) {
  return (
    <div className={`relative shrink-0 ${ring ? "ring-2 ring-[hsl(var(--pc-gold))] ring-offset-2 ring-offset-[hsl(var(--pc-ink))]" : ""} rounded-full`}>
      {p.avatar ? (
        <img src={p.avatar} alt={p.full_name} className="h-11 w-11 rounded-full object-cover" />
      ) : (
        <div className="h-11 w-11 rounded-full bg-[hsl(var(--secondary))] text-white grid place-items-center text-sm font-semibold">
          {initials(p.full_name)}
        </div>
      )}
    </div>
  );
}

export function DemoUserSwitcher({ variant = "floating" }) {
  const { personas, currentPersona, switchPersona } = useApp();
  const [open, setOpen] = useState(false);

  if (!currentPersona) return null;

  const handleSelect = (p) => {
    switchPersona(p.id);
    setOpen(false);
    toast.success(`Now viewing as ${p.full_name}`, {
      description: p.is_plus_one ? `Guest of ${p.primary_name}` : p.group_names?.join(" · "),
    });
  };

  const Trigger =
    variant === "compact" ? (
      <button
        data-testid="demo-user-switcher-trigger"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-white px-2.5 py-1.5 text-sm font-medium shadow-sm hover:shadow transition-shadow"
      >
        <Avatar p={currentPersona} />
        <span className="hidden sm:block max-w-[9rem] truncate">{currentPersona.full_name}</span>
        <ChevronsUpDown className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
      </button>
    ) : (
      <button
        data-testid="demo-user-switcher-trigger"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 rounded-full bg-white/90 backdrop-blur border border-white/70 pl-1.5 pr-3 py-1.5 shadow-[0_8px_24px_hsl(189_62%_4%/0.5)] hover:bg-white transition-colors"
      >
        <Avatar p={currentPersona} />
        <div className="text-left leading-tight">
          <div className="text-[11px] uppercase tracking-wider text-[hsl(38_50%_40%)] font-bold flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Demo
          </div>
          <div className="text-sm font-semibold text-[hsl(var(--primary))] max-w-[7.5rem] truncate">
            {currentPersona.first_name} {currentPersona.last_name}
          </div>
        </div>
        <ChevronsUpDown className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
      </button>
    );

  const sheet = (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100]" data-testid="demo-user-switcher-sheet">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-[hsl(189_62%_4%/0.6)] backdrop-blur-sm"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-[30rem] rounded-t-3xl att-bg border-t border-[hsl(var(--pc-line))] max-h-[85vh] overflow-y-auto no-scrollbar"
          >
            <div className="px-5 pb-8 pt-4">
              <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[hsl(var(--pc-line))]" />
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-serif text-2xl text-cream flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-[hsl(var(--pc-gold))]" /> Switch persona
                </h3>
                <button onClick={() => setOpen(false)} className="text-cream-dim hover:text-cream p-1"><X className="h-5 w-5" /></button>
              </div>
              <p className="text-sm text-cream-dim mb-4">Preview the personalized experience for each attendee group.</p>

              <div className="space-y-2.5">
                {personas.map((p) => {
                  const active = p.id === currentPersona.id;
                  return (
                    <button
                      key={p.id}
                      data-testid="demo-user-switcher-persona-option"
                      onClick={() => handleSelect(p)}
                      className={`w-full flex items-center gap-3 rounded-2xl border p-3 text-left transition-colors ${
                        active
                          ? "border-[hsl(var(--pc-gold)/0.6)] bg-[hsl(var(--pc-gold)/0.12)]"
                          : "border-[hsl(var(--pc-line))] att-glass hover:bg-[hsl(var(--pc-surface2)/0.7)]"
                      }`}
                    >
                      <Avatar p={p} ring={active} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-cream truncate">{p.full_name}</span>
                          {p.is_plus_one && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[hsl(var(--mint))]">
                              <UserPlus className="h-3 w-3" /> Guest
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-cream-dim truncate">
                          {p.is_plus_one ? `Guest of ${p.primary_name}` : p.title}
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {p.is_plus_one ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full border border-[hsl(var(--pc-line))] bg-[hsl(var(--pc-cream)/0.08)] text-cream-dim">Plus-one</span>
                          ) : (
                            p.groups?.map((g) => <GroupBadge key={g.id} groupId={g.id} groupName={g.name} size="xs" dark />)
                          )}
                        </div>
                      </div>
                      {active && <Check className="h-5 w-5 text-[hsl(var(--pc-gold))] shrink-0" data-testid="demo-user-switcher-active-persona" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {Trigger}
      {typeof document !== "undefined" && createPortal(sheet, document.body)}
    </>
  );
}
